import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const STORAGE_KEY = 'workflow-canvas:v1';
const LOCAL_SAVES_KEY = 'workflow-canvas:local-saves';
const SHARE_PREFIX = '#workflow=';
const SHARE_LIMIT = 7000;
const MIN_NODE_WIDTH = 120;
const MIN_NODE_HEIGHT = 72;
const PRESETS = [
  '#ffffff',
  '#f8fafc',
  '#dbeafe',
  '#dcfce7',
  '#fef3c7',
  '#fee2e2',
  '#f3e8ff',
  '#111827'
];
const BACKGROUNDS = ['grid', 'white', 'black'];

const initialWorkflow = {
  nodes: [
    {
      id: 'node-start',
      x: 260,
      y: 220,
      width: 180,
      height: 96,
      label: 'Start',
      backgroundColor: '#ffffff',
      textColor: '#111827',
      borderColor: '#2563eb'
    },
    {
      id: 'node-review',
      x: 560,
      y: 360,
      width: 210,
      height: 104,
      label: 'Review request',
      backgroundColor: '#dbeafe',
      textColor: '#172554',
      borderColor: '#60a5fa'
    }
  ],
  edges: [
    {
      id: 'edge-node-start-node-review',
      sourceId: 'node-start',
      targetId: 'node-review',
      color: '#334155'
    }
  ]
};

function cloneWorkflow(workflow) {
  return {
    nodes: workflow.nodes.map((node) => ({ ...node })),
    edges: workflow.edges.map((edge) => ({ ...edge }))
  };
}

function normalizeWorkflow(input) {
  if (!input || !Array.isArray(input.nodes) || !Array.isArray(input.edges)) {
    throw new Error('Workflow JSON must include nodes and edges arrays.');
  }

  const nodes = input.nodes.map((node, index) => ({
    id: String(node.id || `node-${index + 1}`),
    x: Number.isFinite(Number(node.x)) ? Number(node.x) : 160 + index * 40,
    y: Number.isFinite(Number(node.y)) ? Number(node.y) : 160 + index * 40,
    width: Math.max(MIN_NODE_WIDTH, Number(node.width) || 180),
    height: Math.max(MIN_NODE_HEIGHT, Number(node.height) || 96),
    label: String(node.label || 'Untitled box'),
    backgroundColor: node.backgroundColor || '#ffffff',
    textColor: node.textColor || '#111827',
    borderColor: node.borderColor || '#2563eb'
  }));

  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges = input.edges
    .filter((edge) => nodeIds.has(String(edge.sourceId)) && nodeIds.has(String(edge.targetId)))
    .map((edge, index) => ({
      id: String(edge.id || `edge-${index + 1}`),
      sourceId: String(edge.sourceId),
      targetId: String(edge.targetId),
      color: edge.color || '#334155'
    }));

  return { nodes, edges };
}

function encodeWorkflow(workflow) {
  const json = JSON.stringify(workflow);
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeWorkflow(encoded) {
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function loadInitialWorkflow() {
  const hash = window.location.hash || '';
  if (hash.startsWith(SHARE_PREFIX)) {
    try {
      return normalizeWorkflow(decodeWorkflow(hash.slice(SHARE_PREFIX.length)));
    } catch (error) {
      console.warn('Unable to load shared workflow:', error);
    }
  }

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return normalizeWorkflow(JSON.parse(saved));
    }
  } catch (error) {
    console.warn('Unable to load saved workflow:', error);
  }

  return cloneWorkflow(initialWorkflow);
}

function loadLocalSaves() {
  try {
    const saved = JSON.parse(localStorage.getItem(LOCAL_SAVES_KEY) || '[]');
    if (!Array.isArray(saved)) {
      return [];
    }
    return saved
      .map((entry) => ({
        id: String(entry.id || makeId('save')),
        name: String(entry.name || 'Untitled workflow'),
        createdAt: entry.createdAt || new Date().toISOString(),
        updatedAt: entry.updatedAt || entry.createdAt || new Date().toISOString(),
        workflow: normalizeWorkflow(entry.workflow)
      }))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  } catch (error) {
    console.warn('Unable to load local saves:', error);
    return [];
  }
}

function makeId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function getNodeCenter(node) {
  return {
    x: node.x + node.width / 2,
    y: node.y + node.height / 2
  };
}

function edgePoint(fromNode, toNode) {
  const from = getNodeCenter(fromNode);
  const to = getNodeCenter(toNode);
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  if (dx === 0 && dy === 0) {
    return { x: from.x + fromNode.width / 2, y: from.y };
  }

  const scale = Math.min(
    Math.abs((fromNode.width / 2) / (dx || Number.EPSILON)),
    Math.abs((fromNode.height / 2) / (dy || Number.EPSILON))
  );

  return {
    x: from.x + dx * scale,
    y: from.y + dy * scale
  };
}

function cubicPoint(p0, p1, p2, p3, t) {
  const mt = 1 - t;
  return {
    x: mt ** 3 * p0.x + 3 * mt ** 2 * t * p1.x + 3 * mt * t ** 2 * p2.x + t ** 3 * p3.x,
    y: mt ** 3 * p0.y + 3 * mt ** 2 * t * p1.y + 3 * mt * t ** 2 * p2.y + t ** 3 * p3.y
  };
}

function cubicTangent(p0, p1, p2, p3, t) {
  const mt = 1 - t;
  return {
    x: 3 * mt ** 2 * (p1.x - p0.x) + 6 * mt * t * (p2.x - p1.x) + 3 * t ** 2 * (p3.x - p2.x),
    y: 3 * mt ** 2 * (p1.y - p0.y) + 6 * mt * t * (p2.y - p1.y) + 3 * t ** 2 * (p3.y - p2.y)
  };
}

function arrowPolygon(point, angle, size = 18) {
  const tip = {
    x: point.x + Math.cos(angle) * (size * 0.5),
    y: point.y + Math.sin(angle) * (size * 0.5)
  };
  const base = {
    x: point.x - Math.cos(angle) * (size * 0.5),
    y: point.y - Math.sin(angle) * (size * 0.5)
  };
  const wing = size * 0.34;
  const normal = angle + Math.PI / 2;
  const left = {
    x: base.x + Math.cos(normal) * wing,
    y: base.y + Math.sin(normal) * wing
  };
  const right = {
    x: base.x - Math.cos(normal) * wing,
    y: base.y - Math.sin(normal) * wing
  };

  return `${tip.x},${tip.y} ${left.x},${left.y} ${right.x},${right.y}`;
}

function getConnection(edge, nodesById) {
  const source = nodesById.get(edge.sourceId);
  const target = nodesById.get(edge.targetId);
  if (!source || !target) {
    return null;
  }

  const start = edgePoint(source, target);
  const end = edgePoint(target, source);
  const distance = Math.hypot(end.x - start.x, end.y - start.y);
  const curve = Math.min(120, Math.max(30, distance * 0.22));
  const horizontal = Math.abs(end.x - start.x) >= Math.abs(end.y - start.y);
  const c1 = horizontal
    ? { x: start.x + Math.sign(end.x - start.x || 1) * curve, y: start.y }
    : { x: start.x, y: start.y + Math.sign(end.y - start.y || 1) * curve };
  const c2 = horizontal
    ? { x: end.x - Math.sign(end.x - start.x || 1) * curve, y: end.y }
    : { x: end.x, y: end.y - Math.sign(end.y - start.y || 1) * curve };
  const mid = cubicPoint(start, c1, c2, end, 0.5);
  const tangent = cubicTangent(start, c1, c2, end, 0.5);

  return {
    start,
    end,
    c1,
    c2,
    mid,
    angle: Math.atan2(tangent.y, tangent.x),
    path: `M ${start.x} ${start.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${end.x} ${end.y}`
  };
}

function exportJson(workflow) {
  const blob = new Blob([JSON.stringify(workflow, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `workflow-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

function wrapCanvasText(context, text, maxWidth) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';

  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (context.measureText(next).width <= maxWidth) {
      line = next;
      return;
    }
    if (line) {
      lines.push(line);
    }
    line = word;
  });

  if (line) {
    lines.push(line);
  }

  return lines.length ? lines : [''];
}

export default function App() {
  const [workflow, setWorkflow] = useState(loadInitialWorkflow);
  const [selected, setSelected] = useState(null);
  const [connectMode, setConnectMode] = useState(false);
  const [connectSourceId, setConnectSourceId] = useState(null);
  const [pan, setPan] = useState({ x: 80, y: 80 });
  const [zoom, setZoom] = useState(1);
  const [backgroundMode, setBackgroundMode] = useState(() => {
    const saved = localStorage.getItem('workflow-canvas:background');
    return BACKGROUNDS.includes(saved) ? saved : 'grid';
  });
  const [isSpaceDown, setIsSpaceDown] = useState(false);
  const [notice, setNotice] = useState('');
  const [isEditingId, setIsEditingId] = useState(null);
  const [localSaves, setLocalSaves] = useState(loadLocalSaves);
  const [saveName, setSaveName] = useState('My workflow');

  const viewportRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);
  const panRef = useRef(null);
  const resizeRef = useRef(null);
  const workflowRef = useRef(workflow);
  const panStateRef = useRef(pan);
  const zoomRef = useRef(zoom);
  const historyRef = useRef({ past: [], future: [] });

  useEffect(() => {
    workflowRef.current = workflow;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(workflow));
  }, [workflow]);

  useEffect(() => {
    panStateRef.current = pan;
  }, [pan]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    localStorage.setItem('workflow-canvas:background', backgroundMode);
  }, [backgroundMode]);

  useEffect(() => {
    localStorage.setItem(LOCAL_SAVES_KEY, JSON.stringify(localSaves));
  }, [localSaves]);

  const selectedNode = useMemo(
    () => (selected?.type === 'node' ? workflow.nodes.find((node) => node.id === selected.id) : null),
    [selected, workflow.nodes]
  );

  const selectedEdge = useMemo(
    () => (selected?.type === 'edge' ? workflow.edges.find((edge) => edge.id === selected.id) : null),
    [selected, workflow.edges]
  );

  const nodesById = useMemo(() => new Map(workflow.nodes.map((node) => [node.id, node])), [workflow.nodes]);

  const pushPast = useCallback((snapshot) => {
    const history = historyRef.current;
    history.past = [...history.past.slice(-59), cloneWorkflow(snapshot)];
    history.future = [];
  }, []);

  const commitWorkflow = useCallback(
    (updater) => {
      setWorkflow((current) => {
        const next = typeof updater === 'function' ? updater(cloneWorkflow(current)) : updater;
        if (JSON.stringify(next) !== JSON.stringify(current)) {
          pushPast(current);
        }
        return normalizeWorkflow(next);
      });
    },
    [pushPast]
  );

  const setWorkflowLive = useCallback((updater) => {
    setWorkflow((current) => normalizeWorkflow(typeof updater === 'function' ? updater(cloneWorkflow(current)) : updater));
  }, []);

  const undo = useCallback(() => {
    const history = historyRef.current;
    if (!history.past.length) {
      return;
    }
    const previous = history.past.pop();
    history.future.push(cloneWorkflow(workflowRef.current));
    setWorkflow(cloneWorkflow(previous));
    setSelected(null);
    setConnectSourceId(null);
  }, []);

  const redo = useCallback(() => {
    const history = historyRef.current;
    if (!history.future.length) {
      return;
    }
    const next = history.future.pop();
    history.past.push(cloneWorkflow(workflowRef.current));
    setWorkflow(cloneWorkflow(next));
    setSelected(null);
    setConnectSourceId(null);
  }, []);

  const screenToWorld = useCallback((clientX, clientY) => {
    const rect = viewportRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - panStateRef.current.x) / zoomRef.current,
      y: (clientY - rect.top - panStateRef.current.y) / zoomRef.current
    };
  }, []);

  const addBox = useCallback(() => {
    const rect = viewportRef.current?.getBoundingClientRect();
    const center = rect
      ? screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2)
      : { x: 280, y: 220 };
    const id = makeId('node');
    commitWorkflow((current) => ({
      ...current,
      nodes: [
        ...current.nodes,
        {
          id,
          x: Math.round(center.x - 90),
          y: Math.round(center.y - 48),
          width: 180,
          height: 96,
          label: 'New box',
          backgroundColor: '#ffffff',
          textColor: '#111827',
          borderColor: '#2563eb'
        }
      ]
    }));
    setSelected({ type: 'node', id });
    setNotice('Box added');
  }, [commitWorkflow, screenToWorld]);

  const deleteSelected = useCallback(() => {
    if (!selected) {
      return;
    }

    commitWorkflow((current) => {
      if (selected.type === 'node') {
        return {
          nodes: current.nodes.filter((node) => node.id !== selected.id),
          edges: current.edges.filter((edge) => edge.sourceId !== selected.id && edge.targetId !== selected.id)
        };
      }
      return {
        ...current,
        edges: current.edges.filter((edge) => edge.id !== selected.id)
      };
    });

    setSelected(null);
    setConnectSourceId((id) => (id === selected.id ? null : id));
  }, [commitWorkflow, selected]);

  const updateNode = useCallback(
    (nodeId, patch) => {
      commitWorkflow((current) => ({
        ...current,
        nodes: current.nodes.map((node) => (node.id === nodeId ? { ...node, ...patch } : node))
      }));
    },
    [commitWorkflow]
  );

  const updateEdge = useCallback(
    (edgeId, patch) => {
      commitWorkflow((current) => ({
        ...current,
        edges: current.edges.map((edge) => (edge.id === edgeId ? { ...edge, ...patch } : edge))
      }));
    },
    [commitWorkflow]
  );

  const startPanning = useCallback((event, force = false) => {
    if (event.button !== 0 && event.button !== 1) {
      return false;
    }
    if (!force && event.button !== 1 && !isSpaceDown) {
      return false;
    }
    event.preventDefault();
    panRef.current = {
      pointerId: event.pointerId,
      start: { x: event.clientX, y: event.clientY },
      original: { ...panStateRef.current }
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    return true;
  }, [isSpaceDown]);

  const handleNodePointerDown = useCallback(
    (event, node) => {
      if (isSpaceDown || event.button === 1) {
        startPanning(event);
        return;
      }

      event.stopPropagation();
      if (event.button !== 0) {
        return;
      }

      if (connectMode) {
        setSelected({ type: 'node', id: node.id });
        setConnectSourceId((sourceId) => {
          if (!sourceId) {
            setNotice('Choose a target box');
            return node.id;
          }
          if (sourceId === node.id) {
            setNotice('Choose a different target');
            return sourceId;
          }
          commitWorkflow((current) => {
            const exists = current.edges.some((edge) => edge.sourceId === sourceId && edge.targetId === node.id);
            if (exists) {
              return current;
            }
            return {
              ...current,
              edges: [
                ...current.edges,
                {
                  id: makeId('edge'),
                  sourceId,
                  targetId: node.id,
                  color: '#334155'
                }
              ]
            };
          });
          setNotice('Connection added');
          return null;
        });
        return;
      }

      setSelected({ type: 'node', id: node.id });
      setIsEditingId(null);
      const start = screenToWorld(event.clientX, event.clientY);
      dragRef.current = {
        pointerId: event.pointerId,
        nodeId: node.id,
        start,
        original: { x: node.x, y: node.y },
        snapshot: cloneWorkflow(workflowRef.current),
        moved: false
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [commitWorkflow, connectMode, isSpaceDown, screenToWorld, startPanning]
  );

  const handleNodePointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      const point = screenToWorld(event.clientX, event.clientY);
      const dx = point.x - drag.start.x;
      const dy = point.y - drag.start.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
        drag.moved = true;
      }

      setWorkflowLive((current) => ({
        ...current,
        nodes: current.nodes.map((node) =>
          node.id === drag.nodeId
            ? {
                ...node,
                x: Math.round(drag.original.x + dx),
                y: Math.round(drag.original.y + dy)
              }
            : node
        )
      }));
    },
    [screenToWorld, setWorkflowLive]
  );

  const finishLiveChange = useCallback(() => {
    const active = dragRef.current || resizeRef.current;
    if (active?.moved) {
      pushPast(active.snapshot);
    }
    dragRef.current = null;
    resizeRef.current = null;
  }, [pushPast]);

  const handleNodePointerUp = useCallback(
    (event) => {
      if (dragRef.current?.pointerId === event.pointerId) {
        finishLiveChange();
      }
    },
    [finishLiveChange]
  );

  const handleResizePointerDown = useCallback(
    (event, node) => {
      event.stopPropagation();
      setSelected({ type: 'node', id: node.id });
      const start = screenToWorld(event.clientX, event.clientY);
      resizeRef.current = {
        pointerId: event.pointerId,
        nodeId: node.id,
        start,
        original: { width: node.width, height: node.height },
        snapshot: cloneWorkflow(workflowRef.current),
        moved: false
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [screenToWorld]
  );

  const handleResizePointerMove = useCallback(
    (event) => {
      const resize = resizeRef.current;
      if (!resize || resize.pointerId !== event.pointerId) {
        return;
      }

      const point = screenToWorld(event.clientX, event.clientY);
      const width = Math.max(MIN_NODE_WIDTH, Math.round(resize.original.width + point.x - resize.start.x));
      const height = Math.max(MIN_NODE_HEIGHT, Math.round(resize.original.height + point.y - resize.start.y));
      resize.moved = true;
      setWorkflowLive((current) => ({
        ...current,
        nodes: current.nodes.map((node) => (node.id === resize.nodeId ? { ...node, width, height } : node))
      }));
    },
    [screenToWorld, setWorkflowLive]
  );

  const handleViewportPointerDown = useCallback(
    (event) => {
      if (event.button === 0) {
        setSelected(null);
        setConnectSourceId(null);
        setIsEditingId(null);
        startPanning(event, true);
        return;
      }
      if (startPanning(event)) {
        return;
      }
    },
    [startPanning]
  );

  const handleViewportPointerMove = useCallback((event) => {
    const activePan = panRef.current;
    if (!activePan || activePan.pointerId !== event.pointerId) {
      return;
    }
    setPan({
      x: activePan.original.x + event.clientX - activePan.start.x,
      y: activePan.original.y + event.clientY - activePan.start.y
    });
  }, []);

  const handleViewportPointerUp = useCallback((event) => {
    if (panRef.current?.pointerId === event.pointerId) {
      panRef.current = null;
    }
  }, []);

  const handleWheel = useCallback((event) => {
    event.preventDefault();
    if (event.ctrlKey || event.metaKey) {
      const rect = viewportRef.current.getBoundingClientRect();
      const before = {
        x: (event.clientX - rect.left - panStateRef.current.x) / zoomRef.current,
        y: (event.clientY - rect.top - panStateRef.current.y) / zoomRef.current
      };
      const nextZoom = Math.min(2, Math.max(0.35, zoomRef.current * (event.deltaY > 0 ? 0.92 : 1.08)));
      setZoom(nextZoom);
      setPan({
        x: event.clientX - rect.left - before.x * nextZoom,
        y: event.clientY - rect.top - before.y * nextZoom
      });
      return;
    }

    setPan((current) => ({
      x: current.x - event.deltaX,
      y: current.y - event.deltaY
    }));
  }, []);

  const handleLabelChange = useCallback(
    (nodeId, label) => {
      setWorkflowLive((current) => ({
        ...current,
        nodes: current.nodes.map((node) => (node.id === nodeId ? { ...node, label } : node))
      }));
    },
    [setWorkflowLive]
  );

  const handleLabelBlur = useCallback(() => {
    setIsEditingId(null);
  }, []);

  const importJsonFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) {
        return;
      }

      try {
        const text = await file.text();
        const imported = normalizeWorkflow(JSON.parse(text));
        commitWorkflow(imported);
        setSelected(null);
        setConnectMode(false);
        setConnectSourceId(null);
        setNotice('Workflow imported');
      } catch (error) {
        setNotice(error.message || 'Could not import that JSON file');
      }
    },
    [commitWorkflow]
  );

  const resetCanvas = useCallback(() => {
    commitWorkflow(cloneWorkflow(initialWorkflow));
    setPan({ x: 80, y: 80 });
    setZoom(1);
    setSelected(null);
    setConnectMode(false);
    setConnectSourceId(null);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setNotice('Canvas reset');
  }, [commitWorkflow]);

  const copyShareLink = useCallback(async () => {
    try {
      const encoded = encodeWorkflow(workflow);
      const url = `${window.location.origin}${window.location.pathname}${SHARE_PREFIX}${encoded}`;
      if (url.length > SHARE_LIMIT) {
        setNotice('This workflow is too large for a reliable link. Use Download JSON instead.');
        return;
      }
      await navigator.clipboard.writeText(url);
      window.history.replaceState(null, '', `${window.location.pathname}${SHARE_PREFIX}${encoded}`);
      setNotice('Share link copied');
    } catch (error) {
      setNotice('Could not copy the link. Try Download JSON instead.');
    }
  }, [workflow]);

  const saveWorkflowLocally = useCallback(() => {
    const trimmedName = saveName.trim() || `Workflow ${localSaves.length + 1}`;
    const now = new Date().toISOString();
    const existing = localSaves.find((entry) => entry.name.toLowerCase() === trimmedName.toLowerCase());
    const nextEntry = {
      id: existing?.id || makeId('save'),
      name: trimmedName,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      workflow: cloneWorkflow(workflow)
    };

    setLocalSaves((current) => [
      nextEntry,
      ...current.filter((entry) => entry.id !== nextEntry.id)
    ]);
    setNotice(existing ? 'Local save updated' : 'Workflow saved locally');
  }, [localSaves, saveName, workflow]);

  const loadWorkflowSave = useCallback(
    (saveId) => {
      const saved = localSaves.find((entry) => entry.id === saveId);
      if (!saved) {
        setNotice('That local save was not found');
        return;
      }

      commitWorkflow(cloneWorkflow(saved.workflow));
      setSelected(null);
      setConnectMode(false);
      setConnectSourceId(null);
      setSaveName(saved.name);
      setNotice(`Loaded ${saved.name}`);
    },
    [commitWorkflow, localSaves]
  );

  const deleteWorkflowSave = useCallback((saveId) => {
    setLocalSaves((current) => current.filter((entry) => entry.id !== saveId));
    setNotice('Local save deleted');
  }, []);

  const downloadPng = useCallback(() => {
    const viewport = viewportRef.current;
    const rect = viewport.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(rect.width * scale);
    canvas.height = Math.round(rect.height * scale);
    const context = canvas.getContext('2d');
    context.scale(scale, scale);
    context.fillStyle = backgroundMode === 'black' ? '#020617' : backgroundMode === 'white' ? '#ffffff' : '#f8fafc';
    context.fillRect(0, 0, rect.width, rect.height);
    context.save();
    context.translate(pan.x, pan.y);
    context.scale(zoom, zoom);

    const worldLeft = -pan.x / zoom;
    const worldTop = -pan.y / zoom;
    const worldRight = worldLeft + rect.width / zoom;
    const worldBottom = worldTop + rect.height / zoom;
    if (backgroundMode === 'grid') {
      context.strokeStyle = '#e2e8f0';
      context.lineWidth = 1 / zoom;
      const gridSize = 40;
      for (let x = Math.floor(worldLeft / gridSize) * gridSize; x < worldRight; x += gridSize) {
        context.beginPath();
        context.moveTo(x, worldTop);
        context.lineTo(x, worldBottom);
        context.stroke();
      }
      for (let y = Math.floor(worldTop / gridSize) * gridSize; y < worldBottom; y += gridSize) {
        context.beginPath();
        context.moveTo(worldLeft, y);
        context.lineTo(worldRight, y);
        context.stroke();
      }
    }

    workflow.edges.forEach((edge) => {
      const connection = getConnection(edge, nodesById);
      if (!connection) {
        return;
      }
      context.strokeStyle = edge.color;
      context.fillStyle = edge.color;
      context.lineWidth = 2.5 / zoom;
      context.beginPath();
      context.moveTo(connection.start.x, connection.start.y);
      context.bezierCurveTo(connection.c1.x, connection.c1.y, connection.c2.x, connection.c2.y, connection.end.x, connection.end.y);
      context.stroke();

      const size = 10;
      const tip = {
        x: connection.mid.x + Math.cos(connection.angle) * (size * 0.5),
        y: connection.mid.y + Math.sin(connection.angle) * (size * 0.5)
      };
      const base = {
        x: connection.mid.x - Math.cos(connection.angle) * (size * 0.5),
        y: connection.mid.y - Math.sin(connection.angle) * (size * 0.5)
      };
      const wing = size * 0.36;
      const normal = connection.angle + Math.PI / 2;
      context.beginPath();
      context.moveTo(tip.x, tip.y);
      context.lineTo(base.x + Math.cos(normal) * wing, base.y + Math.sin(normal) * wing);
      context.lineTo(base.x - Math.cos(normal) * wing, base.y - Math.sin(normal) * wing);
      context.closePath();
      context.fill();
    });

    workflow.nodes.forEach((node) => {
      context.fillStyle = node.backgroundColor;
      context.strokeStyle = node.borderColor;
      context.lineWidth = 2 / zoom;
      context.beginPath();
      context.roundRect(node.x, node.y, node.width, node.height, 10);
      context.fill();
      context.stroke();
      context.fillStyle = node.textColor;
      context.font = '600 15px "MS Gothic", "Lucida Console", "Courier New", monospace';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      const lines = wrapCanvasText(context, node.label, node.width - 28).slice(0, 4);
      const lineHeight = 20;
      const startY = node.y + node.height / 2 - ((lines.length - 1) * lineHeight) / 2;
      lines.forEach((line, index) => {
        context.fillText(line, node.x + node.width / 2, startY + index * lineHeight);
      });
    });

    context.restore();
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `workflow-${new Date().toISOString().slice(0, 10)}.png`;
    link.click();
    setNotice('PNG downloaded');
  }, [backgroundMode, nodesById, pan, workflow, zoom]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      if (event.code === 'Space' && !isTyping) {
        event.preventDefault();
        setIsSpaceDown(true);
      }

      if (isTyping) {
        return;
      }

      if ((event.key === 'Delete' || event.key === 'Backspace') && selected) {
        event.preventDefault();
        deleteSelected();
      }
      if (event.key === 'Escape') {
        setConnectMode(false);
        setConnectSourceId(null);
        setSelected(null);
        setIsEditingId(null);
      }
      if (event.key.toLowerCase() === 'c') {
        setConnectMode((value) => !value);
        setConnectSourceId(null);
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        redo();
      }
    };

    const handleKeyUp = (event) => {
      if (event.code === 'Space') {
        setIsSpaceDown(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [deleteSelected, redo, selected, undo]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }
    const timeout = window.setTimeout(() => setNotice(''), 2600);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  return (
    <div className="app-shell">
      <header className="toolbar">
        <div className="brand">
          <span className="brand-mark">W</span>
          <div>
            <h1>Workflow Canvas</h1>
            <p>Drag boxes to move. Space + drag to pan. Connect mode: click two boxes.</p>
          </div>
        </div>

        <div className="actions">
          <button type="button" onClick={addBox}>Add Box</button>
          <button
            type="button"
            className={connectMode ? 'active' : ''}
            onClick={() => {
              setConnectMode((value) => !value);
              setConnectSourceId(null);
            }}
          >
            Connect Mode
          </button>
          <button type="button" onClick={downloadPng}>Download PNG</button>
          <button type="button" onClick={() => exportJson(workflow)}>Download JSON</button>
          <button type="button" onClick={() => fileInputRef.current.click()}>Import JSON</button>
          <button type="button" onClick={saveWorkflowLocally}>Save Local</button>
          <button type="button" onClick={copyShareLink}>Copy Share Link</button>
          <button type="button" className="danger" onClick={resetCanvas}>Reset Canvas</button>
          <div className="background-toggle" aria-label="Canvas background">
            {BACKGROUNDS.map((mode) => (
              <button
                key={mode}
                type="button"
                className={backgroundMode === mode ? 'active' : ''}
                onClick={() => setBackgroundMode(mode)}
              >
                {mode}
              </button>
            ))}
          </div>
          <input ref={fileInputRef} type="file" accept="application/json,.json" onChange={importJsonFile} hidden />
        </div>
      </header>

      <main className="workspace">
        <section
          ref={viewportRef}
          className={`viewport bg-${backgroundMode} ${isSpaceDown ? 'panning-ready' : ''} ${connectMode ? 'connect-mode' : ''}`}
          onPointerDown={handleViewportPointerDown}
          onPointerMove={(event) => {
            handleViewportPointerMove(event);
            handleNodePointerMove(event);
            handleResizePointerMove(event);
          }}
          onPointerUp={(event) => {
            handleViewportPointerUp(event);
            handleNodePointerUp(event);
            finishLiveChange();
          }}
          onPointerCancel={(event) => {
            handleViewportPointerUp(event);
            finishLiveChange();
          }}
          onWheel={handleWheel}
        >
          <div
            className="world"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`
            }}
          >
            <svg className="edges-layer">
              {workflow.edges.map((edge) => {
                const connection = getConnection(edge, nodesById);
                if (!connection) {
                  return null;
                }
                const isSelected = selected?.type === 'edge' && selected.id === edge.id;
                return (
                  <g key={edge.id}>
                    <path
                      className="edge-hit"
                      d={connection.path}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        setSelected({ type: 'edge', id: edge.id });
                        setConnectSourceId(null);
                      }}
                    />
                    <path
                      className={`edge ${isSelected ? 'selected' : ''}`}
                      d={connection.path}
                      stroke={edge.color}
                    />
                    <polygon
                      className={`edge-arrow ${isSelected ? 'selected' : ''}`}
                      points={arrowPolygon(connection.mid, connection.angle)}
                      fill={edge.color}
                    />
                  </g>
                );
              })}
            </svg>

            {workflow.nodes.map((node) => {
              const isSelected = selected?.type === 'node' && selected.id === node.id;
              const isConnectSource = connectSourceId === node.id;
              return (
                <div
                  key={node.id}
                  className={`node ${isSelected ? 'selected' : ''} ${isConnectSource ? 'connect-source' : ''}`}
                  style={{
                    left: node.x,
                    top: node.y,
                    width: node.width,
                    height: node.height,
                    backgroundColor: node.backgroundColor,
                    color: node.textColor,
                    borderColor: node.borderColor
                  }}
                  onPointerDown={(event) => handleNodePointerDown(event, node)}
                  onPointerMove={handleNodePointerMove}
                  onPointerUp={handleNodePointerUp}
                  onDoubleClick={(event) => {
                    event.stopPropagation();
                    if (!connectMode && !dragRef.current?.moved) {
                      setIsEditingId(node.id);
                    }
                  }}
                >
                  {isEditingId === node.id ? (
                    <textarea
                      className="node-editor"
                      value={node.label}
                      autoFocus
                      onPointerDown={(event) => event.stopPropagation()}
                      onChange={(event) => handleLabelChange(node.id, event.target.value)}
                      onBlur={handleLabelBlur}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          event.currentTarget.blur();
                        }
                      }}
                    />
                  ) : (
                    <div className="node-label">{node.label}</div>
                  )}
                  <span className="node-id">{node.id}</span>
                  <button
                    type="button"
                    aria-label="Resize box"
                    className="resize-handle"
                    onPointerDown={(event) => handleResizePointerDown(event, node)}
                    onPointerMove={handleResizePointerMove}
                    onPointerUp={finishLiveChange}
                  />
                </div>
              );
            })}
          </div>

          <div className="viewport-status">
            <span>{connectMode ? (connectSourceId ? 'Pick target box' : 'Pick source box') : 'Select, drag, or pan'}</span>
            <span>{Math.round(zoom * 100)}%</span>
          </div>

          <div className="zoom-controls">
            <button type="button" onClick={() => setZoom((value) => Math.max(0.35, value - 0.1))}>-</button>
            <button type="button" onClick={() => setZoom(1)}>100%</button>
            <button type="button" onClick={() => setZoom((value) => Math.min(2, value + 0.1))}>+</button>
          </div>
        </section>

        <aside className="side-panel">
          <div className="panel-section">
            <h2>Selection</h2>
            {!selected && <p className="muted">Select a box or arrow to edit it.</p>}

            {selectedNode && (
              <div className="editor-group">
                <label>
                  Label
                  <textarea
                    value={selectedNode.label}
                    onChange={(event) => handleLabelChange(selectedNode.id, event.target.value)}
                    onBlur={() => updateNode(selectedNode.id, { label: selectedNode.label })}
                  />
                </label>

                <ColorControl
                  label="Background"
                  value={selectedNode.backgroundColor}
                  onChange={(value) => updateNode(selectedNode.id, { backgroundColor: value })}
                />
                <ColorControl
                  label="Text"
                  value={selectedNode.textColor}
                  onChange={(value) => updateNode(selectedNode.id, { textColor: value })}
                />
                <ColorControl
                  label="Border"
                  value={selectedNode.borderColor}
                  onChange={(value) => updateNode(selectedNode.id, { borderColor: value })}
                />

                <div className="metrics">
                  <span>ID</span>
                  <code>{selectedNode.id}</code>
                </div>
                <button type="button" className="danger full-width" onClick={deleteSelected}>Delete Box</button>
              </div>
            )}

            {selectedEdge && (
              <div className="editor-group">
                <ColorControl
                  label="Arrow"
                  value={selectedEdge.color}
                  onChange={(value) => updateEdge(selectedEdge.id, { color: value })}
                />
                <div className="metrics">
                  <span>From</span>
                  <code>{selectedEdge.sourceId}</code>
                </div>
                <div className="metrics">
                  <span>To</span>
                  <code>{selectedEdge.targetId}</code>
                </div>
                <button type="button" className="danger full-width" onClick={deleteSelected}>Delete Arrow</button>
              </div>
            )}
          </div>

          <div className="panel-section local-saves">
            <h2>Local Saves</h2>
            <label className="save-name">
              Save name
              <input
                type="text"
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    saveWorkflowLocally();
                  }
                }}
              />
            </label>
            <button type="button" className="full-width" onClick={saveWorkflowLocally}>Save Current Workflow</button>
            {localSaves.length === 0 ? (
              <p className="muted">No named local saves yet.</p>
            ) : (
              <div className="save-list">
                {localSaves.map((entry) => (
                  <div className="save-row" key={entry.id}>
                    <div>
                      <strong>{entry.name}</strong>
                      <span>{new Date(entry.updatedAt).toLocaleString()}</span>
                    </div>
                    <div className="save-actions">
                      <button type="button" onClick={() => loadWorkflowSave(entry.id)}>Load</button>
                      <button type="button" className="danger" onClick={() => deleteWorkflowSave(entry.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="panel-section stats">
            <h2>Workflow</h2>
            <div><strong>{workflow.nodes.length}</strong><span>boxes</span></div>
            <div><strong>{workflow.edges.length}</strong><span>arrows</span></div>
          </div>

          <div className="panel-section shortcuts">
            <h2>Shortcuts</h2>
            <p><kbd>C</kbd> connect mode</p>
            <p><kbd>Delete</kbd> remove selection</p>
            <p><kbd>Esc</kbd> clear mode</p>
            <p><kbd>Ctrl</kbd> + wheel zooms</p>
            <p><kbd>Ctrl</kbd> + <kbd>Z</kbd> undo</p>
          </div>
        </aside>
      </main>

      {notice && <div className="toast">{notice}</div>}
    </div>
  );
}

function ColorControl({ label, value, onChange }) {
  return (
    <div className="color-control">
      <span>{label}</span>
      <div className="swatches">
        {PRESETS.map((color) => (
          <button
            key={`${label}-${color}`}
            type="button"
            aria-label={`${label} ${color}`}
            className={value.toLowerCase() === color.toLowerCase() ? 'picked' : ''}
            style={{ backgroundColor: color }}
            onClick={() => onChange(color)}
          />
        ))}
      </div>
      <input type="color" value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}
